import dayjs from 'dayjs';
import ArticleService from '@/services/ArticleService';
import { filterMap } from '@/utils/helper';

// state
const localState = {
  autoLoad: {
    loading: false,
    loader: ArticleService.getAll,
    emptyMap: {},
  },
  articles: {},
  categories: {},
  managementGroups: {},
  mixerGroups: {},
  orderLevels: {},
  productGroups: {},
  locationsDummy: {},
  commissions: {},
  productPositions: {},
  favorites: {},
  currentScreen: {
    type: 'category',
    id: 'all',
  },
  timeTypes: {
    0: 'minutes',
    1: 'hours',
    2: 'days',
  },
  MIXER_ARTICLE: 2,
  TIMER_ARTICLE: 5,
  ALL_SCREEN: 0,
  MINUTE_TYPE: 0,
  HOUR_TYPE: 1,
  DAY_TYPE: 2,
};

// getters
const localGetters = {
  printArticleColor: (state, getters) => (article) => {
    const color = article.color || (article.category && state.categories[article.category] && state.categories[article.category].color);
    return ArticleService.convertColor(color);
  },
  printCategoryColor: () => category => {
    if(!category) return '';
    return ArticleService.convertColor(category.color)
  },
  getArticleMixerGroup: (state, getters) => (article) => {
    let mixerGroupId = null;
    let managementGroup = article.managementGroup
      ? getters.getObject(article.managementGroup, 'managementGroups')
      : null;
    if (managementGroup) {
      const managementGroupClone = getters.getClone(managementGroup, 'managementGroups');
      if (managementGroupClone) {
        managementGroup = managementGroupClone;
      }
    }
    let category = article.category
      ? getters.getObject(article.category, 'categories')
      : null;
    if (category) {
      const categoryClone = getters.getClone(category, 'categories');
      if (categoryClone) {
        category = categoryClone;
      }
    }
    if (article.mixerGroup) {
      mixerGroupId = article.mixerGroup;
    } else if (managementGroup && managementGroup.mixerGroup) {
      mixerGroupId = managementGroup.mixerGroup;
    } else if (category && category.mixerGroup) {
      mixerGroupId = category.mixerGroup;
    }
    return mixerGroupId;
  },
  getArticleMixers: (state, getters) => (article) => {
    const cloneList = getters.getCloneArticleList;
    const mixerGroupId = getters.getArticleMixerGroup(article);
    if(!mixerGroupId) return [];
    const mixers = [];
    const mixerGroup = state.mixerGroups[mixerGroupId];
    if (mixerGroup && mixerGroup.products) {
      mixerGroup.products.forEach((mixerId) => {
        let article = Object.assign({}, state.articles[mixerId]);
        if (article) {
          const clone = getters.getArticleClone(article, cloneList);
          if (clone) {
            article.name = clone.name;
            article.price = clone.price;
            article.cost = clone.cost;
            article.color = clone.color;
          }
          mixers.push({
            id: article.id,
            name: article.name,
            price: article.price,
            cost: article.cost,
            color: article.color,
            masterId: article.masterId,
          });
        }
      });
    }
    return mixers;
  },
  getScreenArticles: (state, getters) => (screenParam = null) => {
    /* Preload promotions list */
    getters.getArray('promotions');
    const articlesWithClone = getters.getArray('articles', false, false);
    const screen = screenParam || state.currentScreen;
    const unsortedArticleMap = [];
    getters
      .getArray('articles', false, true)
      .forEach((article) => {
        const isNotMixer = article.type != state.MIXER_ARTICLE;
        const clone = getters.getArticleClone(article, articlesWithClone);
        const isVisible = clone ? clone.active : (article.active || article.masterId);
        const color = clone ? clone.color : article.color;
        const category = clone ? clone.category : article.category;
        const name = clone ? clone.name : article.name;
        let inCategory = true;
        if (screen.type == 'category' && screen.id !== 'all') {
          inCategory = category == screen.id;
        }
        if (isNotMixer && isVisible && inCategory && !article.deletedAt) {
          unsortedArticleMap[article.id] = article;
          unsortedArticleMap[article.id].color = color;
          unsortedArticleMap[article.id].category = category;
          unsortedArticleMap[article.id].name = name;
        }
      });
    let sortedArticles = [];
    const sortedArticleMap = [];
    const articlePosition = getters.getArticlePosition(screen);
    if (articlePosition
        && articlePosition.products
        && (
          (!Array.isArray(articlePosition.products) && articlePosition.products != '[]')
              || (Array.isArray(articlePosition.products) && articlePosition.products != [])
        )
    ) {
      const parsedPositions = Array.isArray(articlePosition.products)
        ? articlePosition.products
        : JSON.parse(articlePosition.products);
      parsedPositions.forEach((articleId) => {
        const article = unsortedArticleMap[articleId];
        if (article) {
          sortedArticles.push(article);
          sortedArticleMap.push(articleId);
        }
      });
    }
    if (screen.type == 'category') {
      Object.keys(unsortedArticleMap).forEach((articleId) => {
        if (!sortedArticleMap.includes(parseInt(articleId))) {
          sortedArticles.push(unsortedArticleMap[articleId]);
        }
      });
      const childCategories = getters.getArray('categories')
                              .sort((a, b) => a.indexNumber - b.indexNumber)
                              .filter(category => category.parentId == screen.id)
                              .map(category => {
                                return {
                                  ...category,
                                  isCategory: true
                                }
                              });
      sortedArticles = childCategories.concat(sortedArticles);
    }
    return sortedArticles;
  },
  getArticlePosition: (state, getters) => (screenParam = null) => {
    const screen = screenParam || state.currentScreen;
    return getters
      .getArray('productPositions', false, true)
      .find((productPosition) => {
        if (screen.type == 'category') {
          if (screen.id) {
            return productPosition.category == screen.id;
          }
          return !productPosition.category && !productPosition.favorite;
        }
        return productPosition.favorite == screen.id;
      });
  },
  getArticleClone: (state, getters) => (article, list = null) => {
    let articles = list;
    if (!list) {
      articles = getters.getArray('articles', false, false);
    }
    return articles
      .find(
        a => a.cloneId
                && article.masterId
                && a.cloneId == article.masterId,
      );
  },
  getCloneArticleList: (state, getters) => getters.getArray('articles', false, false).filter(a => a.cloneId),
  getArticleProperty: (state, getters) => (article, property, list = null) => {
    const clone = getters.getArticleClone(article, list);
    return clone ? clone[property] : article[property];
  },
  getArticleCommission: (state, getters) => (articleId) => {
    let commission = null;
    getters
      .getArray('commissions')
      .forEach((c) => {
        if (c.products.length > 0) {
          const articles = Array.isArray(c.products) ? c.products : JSON.parse(c.products);
          if (articles.includes(articleId)) {
            commission = c;
          }
        }
      });
    return commission;
  },
  isTimerArticle: (state, getters) => (articleId) => {
    const article = getters.getObject(articleId, 'articles');
    return (article && article.type) === state.TIMER_ARTICLE;
  },
  getDatetime: () => (datetime) => {
    const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

    const date = datetime ? dayjs(datetime) : dayjs();

    return date.format(DATE_TIME_FORMAT);
  },
  getTimerArticleQty: (state, getters) => (item) => {
    const start = dayjs(item.timeStart);
    const timeEnd = getters.getDatetime(item.timeEnd);
    const end = dayjs(timeEnd);

    const articleId = item.product || item.id;
    const article = getters.getObject(articleId, 'articles');
    const qty = end.diff(start, state.timeTypes[article.timeType], true);

    if (qty < 0) {
      return 0;
    }

    if (article.timeType === state.MINUTE_TYPE) {
      return Math.ceil(qty);
    }

    return Math.round(qty * 10) / 10;
  },
  getTimerArticleTotal: (state, getters) => (item) => {
    if (!item.timeEnd) return 0;

    const articleId = item.product || item.id;
    const qty = getters.getTimerArticleQty(item);
    const article = getters.getObject(articleId, 'articles');
    const mode = item.id ? 'old' : 'new';
    const commission = getters.getCommissionOfItem(item, mode);
    const promotion = getters.getPromotionByArticle(article);

    let price = getters.getArticleProperty(article, 'price');
    let priceMin = null;
    if (commission) {
      if (qty > 1) {
        commission.qty = qty;
      }
      price = item.price;

      const commissionPrice = commission.commissionIncludePrice ? 0 : Number(commission.price);
      priceMin = price + commissionPrice;
    } else if (promotion) {
      price = getters.getPromotionPrice(promotion, price);
    }
    const total = price * qty;

    return getters.calTotal(article, total, priceMin);
  },
  calTotal: state => (article, total, priceMin = null) => {
    if (article.type === state.TIMER_ARTICLE) {
      const min = priceMin || article.priceMin;
      return (total > min) ? total : min;
    }
    return total;
  }
};

export default {
  state: localState,
  getters: localGetters,
};
